import Phaser from "phaser";
import { BaseModal } from "@phaser/ui/modals/BaseModal";
import {
  SArrowBtn,
  ArrowDirection,
  ArrowRole,
} from "@phaser/ui/btns/SArrowBtn";
import { HELP_PAGES } from "@phaser/ui/modals/helpPages";
import { getCurrentLanguage, t } from "@/i18n/i18n";

type PageIndex = 0 | 1 | 2 | 3 | 4;

export class HelpModal extends BaseModal {
  private panelW: number;
  private panelH: number;

  private currentPage: PageIndex = 0;

  private illustImg!: Phaser.GameObjects.Image;
  private descText!: Phaser.GameObjects.Text;

  private prevBtn!: SArrowBtn;
  private nextBtn!: SArrowBtn;
  private doneBtn!: SArrowBtn;

  constructor(
    scene: Phaser.Scene,
    width: number,
    height: number,
    onClose?: () => void,
  ) {
    super(scene, { width, height, onClose });

    this.panelW = (width * 681) / 768;
    this.panelH = (height * 883) / 1366;

    this.buildUI(width, height, onClose);
    this.setDepth(9999);

    this.setPage(0);
  }

  private buildUI(width: number, height: number, onClose?: () => void) {
    // 패널
    const panel = this.scene.add
      .image(width / 2, height / 2, "modal-bg")
      .setDisplaySize(this.panelW, this.panelH)
      .setOrigin(0.5);

    // 배경 사각형
    const illustBg = this.scene.add
      .rectangle(width / 2, height / 2 - this.panelH * 0.22, 520, 320, 0x1c120e)
      .setOrigin(0.5);

    // 일러스트(이미지) - 초기 텍스처는 0페이지
    this.illustImg = this.scene.add
      .image(
        width / 2,
        height / 2 - this.panelH * 0.22,
        HELP_PAGES[0].illustKey,
      )
      .setOrigin(0.5);

    // 설명 텍스트
    const isEnglish = getCurrentLanguage() === "en";

    this.descText = this.scene.add
      .text(width / 2, height / 2 - this.panelH * 0.02, "", {
        fontFamily: "Black Han Sans",
        fontSize: isEnglish ? "36px" : "42px",
        color: "#1C120E",
        align: "center",
        wordWrap: {
          width: this.panelW * 0.78,
          useAdvancedWrap: true,
        },
      })
      .setOrigin(0.5, 0);

    // 버튼 위치
    const btnY = height / 2 + this.panelH * 0.4;
    const leftX = width / 2 - this.panelW * 0.38;
    const rightX = width / 2 + this.panelW * 0.38;

    // 이전
    this.prevBtn = new SArrowBtn(this.scene, leftX, btnY, {
      direction: ArrowDirection.LEFT,
      role: ArrowRole.PREV,
      onClick: () => this.prevPage(),
    });

    // 다음
    this.nextBtn = new SArrowBtn(this.scene, rightX, btnY, {
      direction: ArrowDirection.RIGHT,
      role: ArrowRole.NEXT,
      onClick: () => this.nextPage(),
    });

    // 완료(마지막 페이지에서만)
    this.doneBtn = new SArrowBtn(this.scene, rightX, btnY, {
      direction: ArrowDirection.RIGHT,
      role: ArrowRole.DONE,
      onClick: () => {
        onClose?.();
        this.close();
      },
    });
    this.doneBtn.setVisible(false);

    this.add([
      panel,
      illustBg,
      this.illustImg,
      this.descText,
      this.prevBtn,
      this.nextBtn,
      this.doneBtn,
    ]);
  }

  private setPage(page: PageIndex) {
    this.currentPage = page;
    const data = HELP_PAGES[page];

    // 일러스트/설명 갱신
    this.illustImg.setTexture(data.illustKey);
    this.descText.setText(t(data.descKey));

    // 버튼 노출
    const isFirst = page === 0;
    const isLast = page === 4;

    this.prevBtn.setVisible(!isFirst);
    this.nextBtn.setVisible(!isLast);
    this.doneBtn.setVisible(isLast);
  }

  private nextPage() {
    if (this.currentPage === 4) return;
    this.setPage((this.currentPage + 1) as PageIndex);
  }

  private prevPage() {
    if (this.currentPage === 0) return;
    this.setPage((this.currentPage - 1) as PageIndex);
  }
}
